import React from 'react';
import './bloodwork-display.css';
import { Card, Table, Button, Dropdown } from 'react-bootstrap';
import Moment from 'react-moment';
import { observer, inject } from 'mobx-react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Client,
  FurtherConsiderations,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkDetailsModal } from './bloodwork-details-modal';
import { BloodworkTestAnalysis } from './bloodwork-test-analysis';
import { UserProfile } from '../../models/UserProfile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnalytics } from '@fortawesome/pro-solid-svg-icons';
import { faAnalytics as faAnalyticsLight } from '@fortawesome/pro-light-svg-icons';
import { faEye, faTable } from '@fortawesome/pro-regular-svg-icons';
import { GenericModal } from '../common/generic-modal';

//This file is for the Home page dashboard

type Props = {
  testResultsPanelLabel: string;
  showPreviousOnCharts?: boolean;
  bloodworks: Bloodwork[];
  bloodworkEntryStore: BloodworkEntryStore;
  currentUserProfile: UserProfile;
  refreshBloodworks: () => Promise<Bloodwork[]>;
  loadClientBloodworks?: (clientId: string) => Promise<Bloodwork[]>;
  furtherConsiderations: FurtherConsiderations;
  allowLoadMoreTestResults?: boolean;
  hideEditTestResultsButton?: boolean;
  history?: any;
  showAnalyzeTableButton?: boolean;
  updateClient?: (client: Client) => Promise<Client>;
  currentClients?: Client[];
};

type State = {
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  bloodwork: Bloodwork;
  previousBloodwork: Bloodwork;
  showBloodworkDetailsModal: boolean;
  showUpgradeModal: boolean;
};

@inject('bloodworkEntryStore')
@observer
export class BloodworkDisplay extends React.Component<Props, State> {
  state: State = {
    collectionDate: '',
    bloodworkAnalysis: new BloodworkAnalysis(),
    bloodwork: undefined,
    previousBloodwork: undefined,
    showBloodworkDetailsModal: false,
    showUpgradeModal: false,
  };

  bloodworkRanges: BloodworkRange[] = [];

  componentDidMount() {
    console.log('BloodworkDisplay.componentDidMount....');
    const { refreshBloodworks, bloodworkEntryStore, allowLoadMoreTestResults } =
      this.props;
    if (allowLoadMoreTestResults) {
      refreshBloodworks().then((bloodworks: Bloodwork[]) => {
        console.log(
          '****************** about to load bloodwork ranges **********',
        );
        return bloodworkEntryStore
          .loadBloodworkRanges()
          .then((bloodworkRanges: BloodworkRange[]) => {
            this.bloodworkRanges = bloodworkRanges;
            console.log('********************* bloodworkRanges **********');
            console.log(this.bloodworkRanges);
            return bloodworks;
          });
      });
    }
  }

  analyze = (
    bloodwork: Bloodwork,
    index: number,
    compare?: boolean,
    comparisonBloodwork?: Bloodwork,
  ) => {
    console.log('inside analyze.....');
    console.log(bloodwork);
    const { bloodworks, showPreviousOnCharts } = this.props;
    let previousBloodwork: Bloodwork;

    if (showPreviousOnCharts && compare) {
      if (comparisonBloodwork) {
        previousBloodwork = comparisonBloodwork;
      } else if (index + 1 <= bloodworks.length) {
        previousBloodwork = bloodworks[index + 1];
      }
    }

    this.setState({
      bloodwork,
      previousBloodwork,
    });
  };

  loadMoreBloodworks = () => {
    this.setState({
      bloodwork: undefined,
    });

    const { bloodworks, bloodworkEntryStore } = this.props;
    const numBloodworks = bloodworks.length;
    const lastCreateDate = bloodworks[numBloodworks - 1].createDate;
    this.toggleLoadMoreResultsButton(true);
    bloodworkEntryStore
      .loadBloodworks(BloodworkEntryStore.DEFAULT_LIMIT, lastCreateDate)
      .then(data => {
        if (!data.LastEvaluatedKey) {
          this.hideLoadMoreResultsButton();
        } else {
          this.toggleLoadMoreResultsButton(false);
        }
      })
      .catch(error => {
        console.error(error);
        this.toggleLoadMoreResultsButton(false);
      });
  };

  hideLoadMoreResultsButton() {
    const element = document.getElementById('loadMoreResultsButton');
    if (element) {
      element.style.display = 'none';
    }
  }

  toggleLoadMoreResultsButton(disable: boolean) {
    const element = document.getElementById('loadMoreResultsButton');
    if (element) {
      if (disable) {
        element.setAttribute('disabled', 'true');
      } else {
        element.removeAttribute('disabled');
      }
    }
  }

  viewResults(bloodwork: Bloodwork) {
    this.setState({
      bloodwork,
      showBloodworkDetailsModal: true,
    });
  }

  onHideBloodworkDetails() {
    this.setState({
      showBloodworkDetailsModal: false,
    });
  }

  editResults(bloodwork: Bloodwork) {
    console.log(bloodwork);
    const { currentUserProfile, bloodworkEntryStore } = this.props;
    if (currentUserProfile.editTestResultsAllowed) {
      // Clear the analyzed bloodwork so the Test Results Analysis panel
      // collapses immediately. Otherwise BloodworkTestAnalysis re-runs
      // analyze() on the prop change and scrollIntoView's back down to the
      // panel — or a pending 1s scroll setTimeout fires after navigation —
      // landing the user on the Analysis section instead of the Edit form.
      this.setState({ bloodwork: undefined, previousBloodwork: undefined });
      bloodworkEntryStore.setCurrentBloodwork(bloodwork);
      bloodworkEntryStore.setHideEnterTestResults(false);
      if (this.props.history) {
        // When editing from Manage Test Results, remember where to return so
        // saveBloodwork can route back to the same client's results instead
        // of leaving the user on the generic entry page.
        const fromManage =
          this.props.history.location &&
          this.props.history.location.pathname === '/bloodwork/analyze';
        if (fromManage) {
          const clientId =
            bloodwork.clientId || (bloodwork.client && bloodwork.client.id);
          this.props.history.push({
            pathname: '/bloodwork/edit',
            state: { returnTo: '/bloodwork/analyze', clientId },
          });
        } else {
          this.props.history.push('/bloodwork/edit');
        }
      }
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  async deleteBloodwork(bloodwork: Bloodwork) {
    const deleteBloodwork = window.confirm(
      'Are you sure you want to delete the test results?',
    );
    if (deleteBloodwork) {
      const { bloodworkEntryStore, loadClientBloodworks } = this.props;
      await bloodworkEntryStore.deleteBloodwork(bloodwork);
      if (loadClientBloodworks) {
        loadClientBloodworks(bloodwork.client.id);
      }
    }
  }

  render() {
    const {
      testResultsPanelLabel,
      bloodworks,
      currentUserProfile,
      bloodworkEntryStore,
      furtherConsiderations,
      hideEditTestResultsButton,
      showPreviousOnCharts,
      showAnalyzeTableButton,
    } = this.props;
    const {
      showUpgradeModal,
      showBloodworkDetailsModal,
      bloodwork,
      previousBloodwork,
    } = this.state;

    if (!bloodworks || !bloodworks.length) {
      return null;
    }

    return (
      <>
        <Card>
          <Card.Header>{testResultsPanelLabel}</Card.Header>
          <Card.Body>
            <div>
              {bloodworks.length > 0 ? (
                <Table
                  striped
                  bordered
                  className="text-nowrap labsmartz-table bloodwork-table"
                >
                  <thead>
                    <tr>
                      <th>Actions</th>
                      <th>Client</th>
                      <th>Sex</th>
                      <th>Collection Date</th>
                      <th>Create Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bloodworks.map((bloodwork: Bloodwork, index: number) => {
                      return (
                        <tr key={index}>
                          <td className="text-nowrap">
                            <Button
                              type="button"
                              title="Analyze Results"
                              style={{ width: 42 }}
                              onClick={() => {
                                this.analyze(bloodwork, index);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faAnalytics}
                                transform={{ x: -1 }}
                              />
                            </Button>
                            &nbsp;&nbsp;
                            {showAnalyzeTableButton ? (
                              <>
                                <Button
                                  type="button"
                                  style={{ width: 42 }}
                                  title="Analyze Results Table"
                                  onClick={() => {
                                    if (this.props.history) {
                                      this.props.history.push({
                                        pathname: '/bloodwork/analyze',
                                        state: {
                                          clientId: bloodwork.client.id,
                                          scrollToComparison: true,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTable}
                                    transform={{ size: 20, y: 1 }}
                                  />
                                </Button>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            {showPreviousOnCharts &&
                            currentUserProfile?.showAnalyzeResultsComparison ? (
                              <>
                                <Dropdown className="d-inline-block">
                                  <Dropdown.Toggle
                                    variant="primary"
                                    title="Analyze Results Comparison"
                                    id={`analyze-split-button-${index}`}
                                    disabled={index === bloodworks.length - 1}
                                  >
                                    <FontAwesomeIcon icon={faAnalyticsLight} />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu className="py-2">
                                    <Dropdown.Header className="px-3 py-1">
                                      Compare with:
                                    </Dropdown.Header>
                                    {bloodworks
                                      .slice(index + 1)
                                      .map((olderBloodwork: Bloodwork) => (
                                        <Dropdown.Item
                                          key={olderBloodwork.id}
                                          className="px-3 py-1"
                                          onClick={() => {
                                            // Blur the focused item before the
                                            // menu closes. Otherwise react-overlays
                                            // restores focus to the dropdown toggle
                                            // on close (toggleElement.focus() in a
                                            // useEffect), and that focus() scrolls
                                            // the toggle — at the top of this panel —
                                            // back into view AFTER our
                                            // scrollToAnalyzeTestResults() runs,
                                            // yanking the page back up instead of
                                            // landing on Test Results Analysis. With
                                            // focus removed, focusInDropdown is false
                                            // on close so the toggle isn't refocused.
                                            (document.activeElement as HTMLElement | null)?.blur();
                                            this.analyze(
                                              bloodwork,
                                              index,
                                              true,
                                              olderBloodwork,
                                            );
                                          }}
                                        >
                                          <Moment format="MM/DD/YYYY">
                                            {olderBloodwork.collectionDate}
                                          </Moment>
                                        </Dropdown.Item>
                                      ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            <Button
                              type="button"
                              style={{ width: 42 }}
                              title="View Results"
                              onClick={() => {
                                this.viewResults(bloodwork);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                transform={{ size: 18, y: 1, x: -1 }}
                              />
                            </Button>
                            &nbsp;&nbsp;
                            {!hideEditTestResultsButton ? (
                              <>
                                <Button
                                  type="button"
                                  style={{ width: 42 }}
                                  title="Edit Results"
                                  onClick={() => {
                                    this.editResults(bloodwork);
                                  }}
                                >
                                  <FontAwesomeIcon icon="edit" />
                                </Button>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            <Button
                              type="button"
                              style={{ width: 42 }}
                              title="Delete Results"
                              onClick={() => {
                                this.deleteBloodwork(bloodwork);
                              }}
                            >
                              <FontAwesomeIcon icon="trash-alt" />
                            </Button>
                          </td>
                          <td className="text-nowrap">
                            {bloodwork.client
                              ? bloodwork.client.properName
                              : ''}
                          </td>
                          <td className="text-nowrap">
                            {bloodwork.client
                              ? bloodwork.client.displayGender
                              : ''}
                          </td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {bloodwork.collectionDate}
                            </Moment>
                          </td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {bloodwork.createDate}
                            </Moment>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div />
              )}
            </div>
            {this.props.allowLoadMoreTestResults ? (
              <div id="load-results-button-container">
                <Button
                  id="loadMoreResultsButton"
                  onClick={() => {
                    this.loadMoreBloodworks();
                  }}
                >
                  Load More Results
                </Button>
              </div>
            ) : null}
          </Card.Body>
        </Card>

        <BloodworkTestAnalysis
          currentUserProfile={currentUserProfile}
          bloodwork={bloodwork}
          previousBloodwork={previousBloodwork}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          furtherConsiderations={furtherConsiderations}
          bloodworkEntryStore={bloodworkEntryStore}
          updateClient={this.props.updateClient}
          currentClients={this.props.currentClients}
        />

        <BloodworkDetailsModal
          show={showBloodworkDetailsModal}
          bloodwork={bloodwork}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          onHide={() => {
            this.onHideBloodworkDetails();
          }}
        />

        <GenericModal
          show={showUpgradeModal}
          title="Edit Test Results Feature Not Available"
          text="Edit Test Results feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />
      </>
    );
  }
}
